import { getAppCallbackRedirectUri } from '@shared/constants/Urls';
import { McpOauthRedirectType } from '@shared/models/mcp/McpServerModel';
import { validateMcpOauthLocalhostPort } from '@shared/models/mcp/mcpValidation';
import type { TFunction } from 'i18next';

/**
 * The callback (redirect URI) shapes an MCP OAuth server can be pointed at, and how each one is spelled.
 * Which one a server needs is a property of ITS provider, not of Cephable: several providers refuse to
 * register an `https://` callback on a domain they haven't approved (waitlist/verification) but will
 * accept a loopback address, which RFC 8252 recommends for native apps anyway.
 *
 * Shared because both ends need the exact same string: the editor shows it for the user to paste into
 * the provider's app registration, and the main process sends it as `redirect_uri` (see mcpOauth).
 */

/** Loopback port used when a server doesn't specify one. High and fixed, so the registered URI is stable. */
export const DEFAULT_MCP_OAUTH_LOCALHOST_PORT = 47821;

/** Path the loopback listener answers on, so a redirect is recognizable in logs. */
export const MCP_OAUTH_CALLBACK_PATH = 'mcp/callback';

export const DEFAULT_MCP_OAUTH_REDIRECT_TYPE: McpOauthRedirectType = 'appCallback';

/**
 * Fall back to the default for any port the editor would have rejected, so a config that slipped through
 * (hand-edited, or saved from an older build) still produces a bindable URI. The range itself lives in
 * one place — {@link validateMcpOauthLocalhostPort} — or the field and the listener could disagree.
 */
export function normalizeMcpOauthLocalhostPort(port?: number): number {
    return validateMcpOauthLocalhostPort(port).valid ? (port as number) : DEFAULT_MCP_OAUTH_LOCALHOST_PORT;
}

/** The exact `redirect_uri` for a server's chosen callback type. */
export function getMcpOauthRedirectUri(type?: McpOauthRedirectType, localhostPort?: number): string {
    switch (type) {
        case 'localhost':
            return `http://localhost:${normalizeMcpOauthLocalhostPort(localhostPort)}/${MCP_OAUTH_CALLBACK_PATH}`;
        default:
            return getAppCallbackRedirectUri();
    }
}

export interface McpOauthRedirectOption {
    value: McpOauthRedirectType;
    label: string;
}

/** The callback choices, in the order they're offered (most compatible first). */
export function mcpOauthRedirectOptions(t: TFunction<'common'>): McpOauthRedirectOption[] {
    return [
        {
            value: 'appCallback',
            label: t('mcp.editor.redirectType.appCallback'),
        },
        {
            value: 'localhost',
            label: t('mcp.editor.redirectType.localhost'),
        },
    ];
}
